import { Component, DestroyRef, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getHexCode } from '../../../core/utilities';
import { TranslateService } from '@ngx-translate/core';
import { EmojiData } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatIconButton } from '@angular/material/button';

@Component({
    selector: 'app-emoji-picker',
    templateUrl: './emoji-picker.component.html',
    styleUrls: ['./emoji-picker.component.scss'],
    standalone: true,
    imports: [
        MatIconButton,
        MatMenuTrigger,
        NgClass,
        MatIcon,
        MatMenu,
        PickerComponent,
    ],
})
export class EmojiPickerComponent implements OnInit {
  @Input() disabled: boolean;
  @Output() emojiSelected: EventEmitter<string> = new EventEmitter<string>();

  emojiPickerTranslations: Object; //object used by emoji-mart for translations

  readonly primaryColor = getHexCode('primary'); //used in HTML

  @ViewChild('emojiMart', { static: false }) emojiMartElement: ElementRef;

  private destroyRef = inject(DestroyRef);

  constructor(public translate: TranslateService) {
  }

  ngOnInit(): void {
    this.translateEmojiPickerElements();

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.translateEmojiPickerElements();
      });
  }

  /**
   * Emits an event that returns the selected emoji as a string (unicode).
   * @param emoji Emoji that has been selected.
   */
  onEmojiSelection(emoji: EmojiData) {
    // array because an emoji can consist of multiple unicodes separated by "-"
    const emojiUnicodes = emoji.unified.split('-').map(code => Number('0x' + code));
    const emojiString = String.fromCodePoint(...emojiUnicodes);
    this.emojiSelected.next(emojiString);
    this.focusSearchField();
  }

  /**
   * Sets the focus to the search field in the emoji-picker.
   */
  focusSearchField() {
    setTimeout(() => {
      this.emojiMartElement['searchRef'].inputRef?.nativeElement?.focus();
    }, 0);
  }

  /**
   * Creates a translation-object that will be passed as "i18n"-argument of emoji-mart.
   */
  translateEmojiPickerElements() {
    this.emojiPickerTranslations = {
      search: this.translate.instant('EMOJI-PICKER.SEARCH'),
      emojilist: this.translate.instant('EMOJI-PICKER.EMOJILIST'),
      notfound: this.translate.instant('EMOJI-PICKER.NOTFOUND'),
      clear: this.translate.instant('EMOJI-PICKER.CLEAR'),
      categories: {
        search: this.translate.instant('EMOJI-PICKER.CATEGORIES.SEARCH'),
        recent: this.translate.instant('EMOJI-PICKER.CATEGORIES.RECENT'),
        people: this.translate.instant('EMOJI-PICKER.CATEGORIES.PEOPLE'),
        nature: this.translate.instant('EMOJI-PICKER.CATEGORIES.NATURE'),
        foods: this.translate.instant('EMOJI-PICKER.CATEGORIES.FOODS'),
        activity: this.translate.instant('EMOJI-PICKER.CATEGORIES.ACTIVITY'),
        places: this.translate.instant('EMOJI-PICKER.CATEGORIES.PLACES'),
        objects: this.translate.instant('EMOJI-PICKER.CATEGORIES.OBJECTS'),
        symbols: this.translate.instant('EMOJI-PICKER.CATEGORIES.SYMBOLS'),
        flags: this.translate.instant('EMOJI-PICKER.CATEGORIES.FLAGS'),
        custom: this.translate.instant('EMOJI-PICKER.CATEGORIES.CUSTOM'),
      },
      skintones: {
        1: this.translate.instant('EMOJI-PICKER.SKINTONES.1'),
        2: this.translate.instant('EMOJI-PICKER.SKINTONES.2'),
        3: this.translate.instant('EMOJI-PICKER.SKINTONES.3'),
        4: this.translate.instant('EMOJI-PICKER.SKINTONES.4'),
        5: this.translate.instant('EMOJI-PICKER.SKINTONES.5'),
        6: this.translate.instant('EMOJI-PICKER.SKINTONES.6'),
      },
    };
  }


}
